import { lazy, Suspense, useSyncExternalStore } from 'react';
import type { FC } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

const AuthShaderCanvas = lazy(() =>
  import('./auth-shader-canvas').then(({ AuthShaderCanvas }) => ({
    default: AuthShaderCanvas
  }))
);

const motionQuery = '(prefers-reduced-motion: reduce)';
const subscribe = (onChange: () => void) => {
  const media = window.matchMedia(motionQuery);
  media.addEventListener('change', onChange);
  document.addEventListener('visibilitychange', onChange);
  return () => {
    media.removeEventListener('change', onChange);
    document.removeEventListener('visibilitychange', onChange);
  };
};
const canAnimate = () =>
  'gpu' in navigator &&
  !window.matchMedia(motionQuery).matches &&
  document.visibilityState === 'visible';
const serverSnapshot = () => false;

/** Decoration never blocks sign-in, including a failed GPU or chunk download. */
export const AuthBackground: FC = () => {
  const animated = useSyncExternalStore(subscribe, canAnimate, serverSnapshot);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 [background:radial-gradient(ellipse_at_5%_12%,var(--z-auth-wave-a),transparent_60%),radial-gradient(ellipse_at_85%_100%,var(--z-auth-wave-d),transparent_60%),var(--z-auth-wave-b)] [.light_&]:opacity-12 [[data-theme=light]_&]:opacity-12"
    >
      {animated ? (
        <ErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <AuthShaderCanvas />
          </Suspense>
        </ErrorBoundary>
      ) : null}
      <div className="absolute inset-0 size-full [background:linear-gradient(180deg,color-mix(in_srgb,var(--z-bg)_55%,transparent),transparent_38%,transparent_72%,color-mix(in_srgb,var(--z-bg)_70%,transparent)),linear-gradient(100deg,color-mix(in_srgb,var(--z-bg)_15%,transparent),color-mix(in_srgb,var(--z-bg)_85%,transparent)_65%)] max-md:[background:color-mix(in_srgb,var(--z-bg)_80%,transparent)]" />
    </div>
  );
};
